import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';

const FadeUp = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut', delay }}>
    {children}
  </motion.div>
);

const ICONS = {
  template: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="40" height="40">
      <path d="M6 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H6ZM15.75 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3H18a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3h-2.25ZM6 12.75a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3v-2.25a3 3 0 0 0-3-3H6ZM17.625 13.5a.75.75 0 0 0-1.5 0v2.625H13.5a.75.75 0 0 0 0 1.5h2.625v2.625a.75.75 0 0 0 1.5 0v-2.625h2.625a.75.75 0 0 0 0-1.5h-2.625V13.5Z" />
    </svg>
  ),
  photo: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="40" height="40">
      <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z" clipRule="evenodd" />
    </svg>
  ),
  personnaliser: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="40" height="40">
      <path d="M18.75 12.75h1.5a.75.75 0 0 0 0-1.5h-1.5a.75.75 0 0 0 0 1.5ZM12 6a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 12 6ZM12 18a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 12 18ZM3.75 6.75h1.5a.75.75 0 1 0 0-1.5h-1.5a.75.75 0 0 0 0 1.5ZM5.25 18.75h-1.5a.75.75 0 0 1 0-1.5h1.5a.75.75 0 0 1 0 1.5ZM3 12a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 3 12ZM9 3.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5ZM12.75 12a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0ZM9 15.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" />
    </svg>
  ),
  telecharger: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="40" height="40">
      <path d="M12 1.5a.75.75 0 0 1 .75.75V7.5h-1.5V2.25A.75.75 0 0 1 12 1.5ZM11.25 7.5v5.69l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72V7.5h3.75a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3v-9a3 3 0 0 1 3-3h3.75Z" />
    </svg>
  ),
};

const ICON_KEYS = ['template', 'photo', 'personnaliser', 'telecharger'];

export default function HowItWorks() {
  const [siteLang, setSiteLang] = useState('FR');
  const navigate = useNavigate();

  const content = {
    FR: {
      title: 'COMMENT ÇA MARCHE',
      sub: 'Créez votre composite professionnel en 4 étapes simples.',
      steps: [
        {
          num: '01', title: 'Choisissez votre template',
          desc: 'Compify propose 8 mises en page professionnelles pour le recto et le verso de votre composite. Chaque template est conçu selon les standards des agences de mannequinat internationales. Sélectionnez celui qui correspond le mieux à votre style.',
          details: ['8 templates recto', '8 templates verso', 'Format A5 standard', 'Adapté impression & digital']
        },
        {
          num: '02', title: 'Ajoutez vos photos',
          desc: 'Importez jusqu\'à 16 photos depuis votre appareil. Glissez-les directement dans les cadres du composite. Recadrez, zoomez et repositionnez chaque photo avec la souris. Compify vérifie automatiquement la résolution pour garantir une impression de qualité.',
          details: ['Jusqu\'à 16 photos', 'Glisser-déposer', 'Recadrage à la souris', 'Vérification résolution HD']
        },
        {
          num: '03', title: 'Personnalisez votre composite',
          desc: 'Renseignez vos informations personnelles — prénom, nom, mensurations, coordonnées, agence. Choisissez votre palette de couleurs parmi 6 thèmes ou créez la vôtre. Ajoutez un contour entre vos photos pour un rendu encore plus pro.',
          details: ['Infos & mensurations', '6 palettes de couleurs', 'Couleurs personnalisées', 'Option contour photos']
        },
        {
          num: '04', title: 'Téléchargez en PDF HD',
          desc: 'En un clic, exportez votre composite en PDF haute résolution au format A5. Les deux faces — recto et verso — sont générées automatiquement dans un seul fichier, prêt pour l\'impression ou l\'envoi par email à une agence.',
          details: ['PDF haute résolution', 'Format A5 (148×210mm)', 'Recto + verso', 'Prêt pour impression']
        },
      ],
      cta: 'CRÉER MON COMPOSITE',
      ctaSub: 'Gratuit · Sans inscription · Sans carte bancaire',
    },
    EN: {
      title: 'HOW IT WORKS',
      sub: 'Create your professional composite card in 4 simple steps.',
      steps: [
        {
          num: '01', title: 'Choose your template',
          desc: 'Compify offers 8 professional layouts for the front and back of your composite. Each template is designed to meet international modeling agency standards. Select the one that best matches your style.',
          details: ['8 front templates', '8 back templates', 'Standard A5 format', 'Print & digital ready']
        },
        {
          num: '02', title: 'Add your photos',
          desc: 'Import up to 16 photos from your device. Drag them directly into the composite frames. Crop, zoom and reposition each photo with your mouse. Compify automatically checks resolution to ensure print quality.',
          details: ['Up to 16 photos', 'Drag and drop', 'Mouse-based cropping', 'HD resolution check']
        },
        {
          num: '03', title: 'Customize your composite',
          desc: 'Enter your personal information — first name, last name, measurements, contact details, agency. Choose your color palette from 6 themes or create your own. Add a border between photos for an even more professional look.',
          details: ['Info & measurements', '6 color palettes', 'Custom colors', 'Photo border option']
        },
        {
          num: '04', title: 'Download as HD PDF',
          desc: 'With one click, export your composite as a high-resolution A5 PDF. Both sides — front and back — are automatically generated in a single file, ready for printing or emailing to an agency.',
          details: ['High-resolution PDF', 'A5 format (148×210mm)', 'Front + back', 'Print ready']
        },
      ],
      cta: 'CREATE MY COMPOSITE',
      ctaSub: 'Free · No sign up · No credit card',
    }
  };

  const d = content[siteLang];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      <Header siteLang={siteLang} setSiteLang={setSiteLang} />

      {/* HERO */}
      <section style={{ backgroundColor: '#0e0e0e', padding: '80px 24px', textAlign: 'center' }}>
        <FadeUp>
          <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '5px', margin: '0 0 16px', fontWeight: '700' }}>COMPIFY</p>
          <h1 style={{ color: '#fff', fontSize: '42px', fontWeight: '900', letterSpacing: '2px', margin: '0 0 20px', textTransform: 'uppercase', lineHeight: 1.1 }}>{d.title}</h1>
          <p style={{ color: '#888', fontSize: '16px', margin: 0 }}>{d.sub}</p>
        </FadeUp>
      </section>

      {/* ÉTAPES */}
      <section style={{ backgroundColor: '#fff', padding: '100px 24px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '80px' }}>
          {d.steps.map((step, i) => (
            <FadeUp key={i} delay={0.1}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center', direction: i % 2 === 0 ? 'ltr' : 'rtl' }}>
                {/* Icône + Numéro + contenu */}
                <div style={{ direction: 'ltr' }}>
                  {/* Icône dans carré fuchsia */}
                  <div style={{
                    width: '64px', height: '64px', backgroundColor: '#FF0066',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '16px',
                  }}>
                    {ICONS[ICON_KEYS[i]]}
                  </div>
                  <p style={{ color: '#FF0066', fontSize: '56px', fontWeight: '900', margin: '0 0 8px', lineHeight: 1, letterSpacing: '2px' }}>{step.num}</p>
                  <div style={{ width: '40px', height: '2px', backgroundColor: '#FF0066', margin: '0 0 24px' }} />
                  <h2 style={{ color: '#1A1A1A', fontSize: '22px', fontWeight: '700', margin: '0 0 16px', letterSpacing: '1px', textTransform: 'uppercase' }}>{step.title}</h2>
                  <p style={{ color: '#777', fontSize: '14px', lineHeight: 1.8, margin: '0 0 24px' }}>{step.desc}</p>
                </div>
                {/* Détails */}
                <div style={{ direction: 'ltr', backgroundColor: i % 2 === 0 ? '#f7f7f7' : '#0e0e0e', padding: '32px' }}>
                  {step.details.map((detail, j) => (
                    <motion.div key={j}
                      initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }} transition={{ delay: j * 0.1 }}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 0', borderBottom: j < step.details.length - 1 ? `1px solid ${i % 2 === 0 ? '#eee' : '#1a1a1a'}` : 'none' }}>
                      <span style={{ color: '#FF0066', fontSize: '16px', fontWeight: '700', flexShrink: 0 }}>✓</span>
                      <span style={{ color: i % 2 === 0 ? '#1A1A1A' : '#fff', fontSize: '13px', fontWeight: '500', letterSpacing: '0.5px' }}>{detail}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              {i < d.steps.length - 1 && <div style={{ height: '1px', backgroundColor: '#f0f0f0', marginTop: '80px' }} />}
            </FadeUp>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: '#FF0066', padding: '80px 24px', textAlign: 'center' }}>
        <FadeUp>
          <motion.button onClick={() => navigate('/editeur')}
            whileHover={{ scale: 1.04, backgroundColor: '#1A1A1A' }} whileTap={{ scale: 0.97 }}
            style={{ backgroundColor: '#fff', color: '#FF0066', border: 'none', padding: '18px 48px', fontSize: '13px', letterSpacing: '3px', fontWeight: '900', cursor: 'pointer', marginBottom: '16px', display: 'block', margin: '0 auto 16px' }}>
            {d.cta}
          </motion.button>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', letterSpacing: '1px', margin: 0 }}>{d.ctaSub}</p>
        </FadeUp>
      </section>

      <Footer siteLang={siteLang} />
    </div>
  );
}